import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';

interface CurvePoint {
  month: number;
  rate: number;
}

interface CurveState {
  product: string;
  points: CurvePoint[];
  original: CurvePoint[];
  loading: boolean;
  hasChanges: boolean;
  
  loadCurve: (product: string) => Promise<void>;
  setPoint: (month: number, rate: number) => void;
  save: () => Promise<void>;
  reset: () => void;
  resetToDefaults: () => Promise<void>;
  copyCurve: (fromProduct: string) => Promise<void>;
}

export const useCurveStore = create<CurveState>((set, get) => ({
  product: 'generic',
  points: [],
  original: [],
  loading: false,
  hasChanges: false,

  loadCurve: async (product: string) => {
    set({ loading: true, product });
    try {
      const { data, error } = await supabase
        .from('product_curves' as any)
        .select('month_index, expected_recovery_rate')
        .eq('product_type', product)
        .order('month_index', { ascending: true });

      if (error) throw error;

      const points = (data || []).map((d: any) => ({
        month: d.month_index,
        rate: parseFloat(d.expected_recovery_rate) * 100 // Convert to percentage
      }));

      set({ 
        points, 
        original: JSON.parse(JSON.stringify(points)),
        hasChanges: false,
        loading: false 
      });
    } catch (error) {
      console.error('Error loading curve:', error);
      set({ loading: false });
    }
  },

  setPoint: (month: number, rate: number) => {
    set(state => {
      const points = state.points.map(p => 
        p.month === month ? { ...p, rate } : p
      );
      const hasChanges = JSON.stringify(points) !== JSON.stringify(state.original);
      return { points, hasChanges };
    });
  },

  save: async () => {
    const { product, points } = get();
    set({ loading: true });

    try {
      const data = points.map(p => ({
        month: p.month,
        rate: p.rate / 100 // Convert back to decimal
      }));

      const { error } = await (supabase as any).rpc('update_curve', {
        _product: product,
        _data: data
      });

      if (error) throw error;

      // Reload to get fresh data
      await get().loadCurve(product);
    } catch (error) {
      console.error('Error saving curve:', error);
      set({ loading: false });
      throw error;
    }
  },

  reset: () => {
    set(state => ({
      points: JSON.parse(JSON.stringify(state.original)),
      hasChanges: false
    }));
  },

  resetToDefaults: async () => {
    const { product } = get();
    set({ loading: true });

    try {
      const { error } = await (supabase as any).rpc('reset_default_curves', {
        _product: product
      });

      if (error) throw error;

      await get().loadCurve(product);
    } catch (error) {
      console.error('Error resetting curve:', error);
      set({ loading: false });
      throw error;
    }
  },

  copyCurve: async (fromProduct: string) => {
    const { product } = get();
    set({ loading: true });

    try {
      const { error } = await (supabase as any).rpc('copy_curve', {
        _from_product: fromProduct,
        _to_product: product
      });

      if (error) throw error;

      await get().loadCurve(product);
    } catch (error) {
      console.error('Error copying curve:', error);
      set({ loading: false });
      throw error;
    }
  }
}));
